import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "../lib/utils-app";
import { Link2, Plus, X, Copy, Trash2, Eye, Lock } from "lucide-react";
import { TID } from "../constants/testIds";
import ShareBuilderDialog from "../components/ShareBuilderDialog";

export default function Shares() {
  const [shares, setShares] = useState([]);
  const [open, setOpen] = useState(false);
  const [openLog, setOpenLog] = useState(null);

  const load = async () => {
    const { data } = await api.get("/shares");
    setShares(data);
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revocare questo link?")) return;
    await api.delete(`/shares/${id}`);
    toast.success("Link revocato");
    load();
  };

  const copyUrl = (token) => {
    const url = `${window.location.origin}/s/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiato: " + url);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Condivisione</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Link2 size={28} /> Link di condivisione
          </h1>
          <p className="text-slate-500 mt-1">Genera link protetti per condividere documenti con clienti, CSE, imprese affidatarie.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid={TID.createShare}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-medium">
          <Plus size={18} /> Nuovo link
        </button>
      </header>

      {shares.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg py-16 text-center text-slate-500">
          <Link2 size={32} className="mx-auto mb-2 text-slate-400" />Nessun link generato.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium">Documenti</th>
                <th className="text-left px-4 py-3 font-medium">Scadenza</th>
                <th className="text-left px-4 py-3 font-medium">Protezione</th>
                <th className="text-left px-4 py-3 font-medium">Stato</th>
                <th className="text-left px-4 py-3 font-medium">Creato</th>
                <th className="text-right px-4 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {shares.map(s => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{s.nome}</td>
                  <td className="px-4 py-3">{s.document_ids?.length || 0}</td>
                  <td className="px-4 py-3">{s.expires_at ? fmtDate(s.expires_at) : "—"}</td>
                  <td className="px-4 py-3">{s.has_password ? <span className="inline-flex items-center gap-1 text-amber-700"><Lock size={12} /> Password</span> : "Nessuna"}</td>
                  <td className="px-4 py-3">
                    {s.revoked ? <span className="status-pill status-expired">Revocato</span> : <span className="status-pill status-valid">Attivo</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(s.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => copyUrl(s.token)} className="text-blue-700 p-1.5" title="Copia link"><Copy size={16} /></button>
                    <button onClick={() => setOpenLog(s)} className="text-slate-700 p-1.5" title="Registro accessi"><Eye size={16} /></button>
                    {!s.revoked && <button onClick={() => revoke(s.id)} className="text-red-600 p-1.5" title="Revoca"><Trash2 size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <ShareBuilderDialog onClose={() => setOpen(false)} onCreated={load} />}
      {openLog && <LogDialog share={openLog} onClose={() => setOpenLog(null)} />}
    </div>
  );
}

function LogDialog({ share, onClose }) {
  const log = share.access_log || [];
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">Registro accessi — {share.nome}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">
          {log.length === 0 ? <div className="text-slate-500 text-sm">Nessun accesso registrato.</div> : (
            <ul className="divide-y divide-slate-100 text-sm">
              {log.slice().reverse().map((l, i) => (
                <li key={`${l.ts}-${l.ip}-${i}`} className="py-2 flex justify-between">
                  <span><span className="font-mono text-xs text-slate-500">{fmtDateTime(l.ts)}</span> — {l.action}</span>
                  <span className="text-slate-500 font-mono text-xs">{l.ip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
